// src/messaging/ui.jsx
// メッセージ画面の小さな部品【2026-10-16】
//   Modal（スマホでは下から出るシート）・認証つき画像・PDF のチップ・通報の入力
import React, { useEffect, useState } from 'react';
import { msgApi } from './api';
import { fileSize } from './util';
import Icon from '../components/Icon';

export function Modal({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="msg-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'msg-modal' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={title}>
        <div className="msg-modal-head">
          <h3>{title}</h3>
          <button type="button" className="msg-icon-btn" onClick={onClose} aria-label="閉じる"><Icon name="x" size={20} /></button>
        </div>
        <div className="msg-modal-body">{children}</div>
        {footer && <div className="msg-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Bearer が要る画像。押すと大きく表示する */
export function AuthImage({ url, alt = '画像', className, zoom = true }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setSrc(null); setFailed(false);
    msgApi.blobUrl(url).then(u => { if (!alive) return; if (u) setSrc(u); else setFailed(true); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [url]);
  if (failed) return <div className={'msg-img-missing ' + (className || '')}>画像を表示できません</div>;
  if (!src) return <div className={'msg-img-loading ' + (className || '')} aria-label="読み込み中" />;
  return (
    <>
      <img src={src} alt={alt} className={'msg-img ' + (className || '')}
           onClick={zoom ? () => setOpen(true) : undefined} style={zoom ? { cursor: 'zoom-in' } : undefined} />
      {open && (
        <div className="msg-lightbox" onClick={() => setOpen(false)} role="dialog" aria-label="画像の拡大">
          <img src={src} alt={alt} />
          <button type="button" className="msg-lightbox-close" aria-label="閉じる"><Icon name="x" size={26} /></button>
        </div>
      )}
    </>
  );
}

/** PDF などのファイル。押すとダウンロードする */
export function FileChip({ file, onError }) {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await msgApi.download(file.url, file.name || 'file.pdf').catch(() => false);
    setBusy(false);
    if (!ok && onError) onError('ファイルを取り出せませんでした');
  };
  return (
    <button type="button" className="msg-file-chip" onClick={get} disabled={busy}>
      <Icon name="file-text" size={18} />
      <span className="msg-file-name">{file.name || 'PDF'}</span>
      {file.size != null && <span className="msg-file-size">{fileSize(file.size)}</span>}
      <Icon name="download" size={16} />
    </button>
  );
}

/**
 * 通報の入力。target は { message_id } / { note_id } / { note_comment_id } / { request_id }
 *   送ったら onDone(サーバーの文) を呼ぶ
 */
export function ReportDialog({ target, what = 'このメッセージ', onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async () => {
    setBusy(true); setErr('');
    const r = await msgApi.post('/reports', { ...target, reason: reason.trim() || undefined });
    setBusy(false);
    if (!r.success) { setErr(r.message || '通報できませんでした'); return; }
    onDone && onDone(r.message || '通報しました');
    onClose();
  };
  return (
    <Modal title="通報する" onClose={onClose}
           footer={<>
             <button type="button" className="btn-secondary" onClick={onClose}>やめる</button>
             <button type="button" className="btn-danger" onClick={send} disabled={busy}>{busy ? '送っています…' : '通報する'}</button>
           </>}>
      <p className="msg-help">{what}を運営者に知らせます。運営者が内容を確認します。相手には知らされません。</p>
      <div className="form-row">
        <label htmlFor="msg-report-reason">理由（なくてもかまいません）</label>
        <textarea id="msg-report-reason" rows={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="例：しつこく誘われる、悪口が書かれている など" />
      </div>
      {err && <p className="msg-error">{err}</p>}
    </Modal>
  );
}

export function Toast({ text, onClose }) {
  useEffect(() => {
    if (!text) return undefined;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [text, onClose]);
  if (!text) return null;
  return <div className="msg-toast" role="status" onClick={onClose}>{text}</div>;
}
